import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Printer, PaperPlaneTilt, Package, XCircle, PencilSimple, CurrencyJpy, Tag, CheckCircle, Desktop, Trash,
  Copy, EnvelopeSimple,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateField } from '@/components/ui/date-time-field';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditShizai, canEditIt, hasCap } from '@/auth/AuthContext';
import LabelPrintDialog from '@/components/labels/LabelPrintDialog';
import { buildProductTagLabel } from '@/components/labels/labelSizes';
import { shizaiApi, ORDER_STATUS_BADGE, ORDER_TYPE_BADGE, APPROVAL_BADGE, yen, packOf } from './shizaiApi';
import ReceiptLineTimeline from './ReceiptLineTimeline';
import { buildOrderMailSubject, buildOrderMailBody, buildOrderMailto } from './orderMailText';
import RegisterAsAssetDialog from './RegisterAsAssetDialog';

export default function OrderDetailDialog({ orderId, onClose, onChanged, onEdit }) {
  const { t } = useT();
  const { user } = useAuth();
  const canWrite = canEditShizai(user);
  const canApproveOrder = hasCap(user, 'shizai_approve');
  const canDeleteOrder = hasCap(user, 'shizai_delete');
  const [order, setOrder] = useState(null);
  const [locations, setLocations] = useState([]);
  const [panel, setPanel] = useState(null); // 'send' | 'receive' | null
  const [sendForm, setSendForm] = useState({ via: 'fax', to: '', note: '', replyBy: '' });
  const [recvForm, setRecvForm] = useState({ location_id: '', lines: {} });
  const [labelData, setLabelData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);

  const mailBody = order ? buildOrderMailBody(order, {
    replyBy: sendForm.replyBy, senderName: user?.name, senderEmail: user?.email,
  }) : '';

  const load = useCallback(() => {
    if (!orderId) return;
    shizaiApi.order(orderId).then(setOrder).catch(() => {});
  }, [orderId]);
  useEffect(() => { load(); setPanel(null); }, [load]);
  useEffect(() => { if (orderId) shizaiApi.locations().then(setLocations).catch(() => {}); }, [orderId]);

  useEffect(() => {
    if (!order) return;
    setSendForm({ via: 'fax', to: order.supplier?.fax || '', note: '', replyBy: '' });
    const shelf = locations.find((l) => l.code === '資材棚');
    setRecvForm({ location_id: shelf?.id || (locations[0]?.id || ''), lines: {} });
  }, [order, locations]);

  if (!orderId) return null;

  const doSend = async () => {
    setBusy(true);
    try { await shizaiApi.sendOrder(orderId, sendForm); toast.success(t('shizai.saved')); setPanel(null); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('shizai.error')); }
    finally { setBusy(false); }
  };
  const doReceive = async () => {
    const lines = Object.entries(recvForm.lines)
      .map(([idx, qty]) => ({ line_index: Number(idx), received_qty: Number(qty) || 0 }))
      .filter((l) => l.received_qty > 0);
    if (lines.length === 0) { toast.error(t('shizai.receive.qtyThisTime')); return; }
    setBusy(true);
    try {
      await shizaiApi.receiveOrder(orderId, { lines, location_id: recvForm.location_id || null });
      toast.success(t('shizai.receive.done')); setPanel(null); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || t('shizai.error')); }
    finally { setBusy(false); }
  };
  const doCancel = async () => {
    if (!window.confirm(`${order.order_no} を取消しますか？`)) return;
    try { await shizaiApi.cancelOrder(orderId); toast.success(t('shizai.saved')); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('shizai.error')); }
  };
  const doDelete = async () => {
    if (!window.confirm(`${order.order_no} を完全に削除しますか？\n\n・紐づく発注依頼の明細は「未発注」に戻ります\n・この操作は元に戻せません`)) return;
    try {
      await shizaiApi.deleteOrder(orderId);
      toast.success(`${order.order_no} を削除しました`);
      onChanged?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || t('shizai.error')); }
  };
  const doApprove = async () => {
    try { await shizaiApi.approveOrder(orderId); toast.success(t('shizai.approval.approved')); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('shizai.error')); }
  };
  const doReject = async () => {
    const reason = window.prompt(t('shizai.approval.rejectPrompt'));
    if (reason === null) return;
    try { await shizaiApi.rejectOrder(orderId, { reason }); toast.success(t('shizai.approval.rejected')); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('shizai.error')); }
  };
  const togglePayment = async () => {
    const next = order.payment_status === 'paid' ? 'unpaid' : 'paid';
    try { await shizaiApi.setPayment(orderId, { payment_status: next }); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('shizai.error')); }
  };
  const printDocs = () => window.open(`/shizai/orders/${orderId}/print`, '_blank');

  const canEditOrder = order && ['draft', 'ordered', 'partially_received'].includes(order.status);
  const canReceive = order && ['ordered', 'partially_received'].includes(order.status);
  const canCancel = order && order.status !== 'received' && order.status !== 'cancelled';
  // Bridge shizai → IT asset. Show the 「IT資産として登録」 button when:
  //   (a) the order has been received (fully or partially) — asset only makes sense once goods are in-hand
  //   (b) the order's 分類 code is one of the IT-purchase-y ones (starts with 'IT' → catches IT / IT-anything future codes)
  //   (c) the caller has it_edit capability (canEditIt)
  const catCode = (order?.order_category_code || '').toUpperCase();
  const isItPurchase = catCode.startsWith('IT');
  const canBridgeToAsset = order
    && (order.status === 'received' || order.status === 'partially_received')
    && isItPurchase
    && canEditIt(user);

  return (
    <>
    <Dialog open={!!orderId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" aria-describedby={undefined} data-testid="order-detail-dialog">
        {!order ? (
          <>
            <DialogHeader><DialogTitle className="sr-only">発注詳細</DialogTitle></DialogHeader>
            <div className="py-16 text-center text-sm text-zinc-400">…</div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 flex-wrap">
                <span className="font-mono text-base">{order.order_no}</span>
                {order.order_type === 'subcontract' && <Badge variant="outline" className={ORDER_TYPE_BADGE.subcontract} data-testid="order-detail-type">{t('shizai.orderType.subcontract')}</Badge>}
                <Badge variant="outline" className={ORDER_STATUS_BADGE[order.status]}>{t(`shizai.orderStatus.${order.status}`)}</Badge>
                <Badge variant="outline" className={order.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-zinc-100 text-zinc-500 border-zinc-200'}>{t(`shizai.pay.${order.payment_status}`)}</Badge>
                {order.approval_status && order.approval_status !== 'not_required' && (
                  <Badge variant="outline" className={APPROVAL_BADGE[order.approval_status]} data-testid="order-approval-badge">{t(`shizai.approval.${order.approval_status}`)}</Badge>
                )}
                {order.overdue && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">納期超過</Badge>}
              </DialogTitle>
            </DialogHeader>

            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><p className="text-[11px] text-zinc-400">{t('shizai.order.supplier')}</p><p className="font-medium text-zinc-800">{order.supplier?.name || '—'}</p></div>
              <div><p className="text-[11px] text-zinc-400">{t('shizai.order.orderDate')}</p><p className="text-zinc-700">{order.order_date || '—'}</p></div>
              <div><p className="text-[11px] text-zinc-400">{t('shizai.order.dueDate')}</p><p className={order.overdue ? 'text-rose-600 font-semibold' : 'text-zinc-700'}>{order.due_date || '—'}</p></div>
              <div><p className="text-[11px] text-zinc-400">{t('shizai.order.deliveryLocation')}</p><p className="text-zinc-700">{order.delivery_location || '—'}</p></div>
              <div><p className="text-[11px] text-zinc-400">発注者</p><p className="text-zinc-700" data-testid="order-detail-buyer">{order.created_by || '—'}</p></div>
              {order.order_category_name && (
                <div><p className="text-[11px] text-zinc-400">受注分類</p><p className="text-zinc-700">{order.order_category_code ? `${order.order_category_code} · ` : ''}{order.order_category_name}</p></div>
              )}
            </div>

            {/* Lines */}
            <div className="rounded-lg border border-zinc-200 overflow-hidden">
              <table className="w-full text-xs">
                <thead><tr className="bg-zinc-100 text-zinc-700 border-b border-zinc-200">
                  <th className="text-left font-semibold px-3 py-2">{t('shizai.req.partNo')}</th>
                  <th className="text-left font-semibold px-3 py-2">{t('shizai.req.name')}</th>
                  <th className="text-right font-semibold px-3 py-2">{t('shizai.req.qty')}</th>
                  <th className="text-right font-semibold px-3 py-2">{t('shizai.order.received')}</th>
                  <th className="text-right font-semibold px-3 py-2">{t('shizai.order.unitPrice')}</th>
                  <th className="text-right font-semibold px-3 py-2">{t('shizai.order.amount')}</th>
                </tr></thead>
                <tbody>
                  {(order.lines || []).map((l, i) => {
                    const pk = packOf(l);
                    return (
                    <tr key={`${l.part_no || l.name || ''}-${i}`} className="border-t border-zinc-200 align-top">
                      <td className="px-3 py-2 font-mono text-zinc-600">{l.part_no || '—'}</td>
                      <td className="px-3 py-2 text-zinc-700">{l.name || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums" data-testid={`order-detail-qty-${i}`}>
                        {pk ? (<><span className="font-medium">{pk.packs} {pk.packUom}</span><span className="block text-[10px] text-zinc-400">= {l.qty} {l.uom}</span></>) : (<>{l.qty} {l.uom}</>)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <div className="whitespace-nowrap">{l.received_qty || 0}</div>
                        {(l.receipts || []).length > 0 && (
                          <div className="mt-1 flex flex-col items-end gap-0" data-testid={`order-line-receipts-${i}`}>
                            <ReceiptLineTimeline
                              receipts={l.receipts}
                              locations={locations}
                              testIdBase={`order-line-timeline-${i}`}
                            />
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {pk ? (<>{yen(pk.packPrice)}<span className="block text-[10px] text-zinc-400">@{yen(l.unit_price)}/{l.uom}</span></>) : yen(l.unit_price)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">{yen(l.amount)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="flex justify-end gap-6 px-3 py-2 bg-zinc-50 border-t border-zinc-200 text-sm">
                <span className="text-zinc-500">{t('shizai.order.subtotal')} <b className="text-zinc-800 tabular-nums">{yen(order.subtotal)}</b></span>
                {(order.shipping_cost || 0) > 0 && (
                  <span className="text-zinc-500" data-testid="order-detail-shipping">送料 <b className="text-zinc-800 tabular-nums">{yen(order.shipping_cost)}</b></span>
                )}
                <span className="text-zinc-500">{t('shizai.order.tax')} <b className="text-zinc-800 tabular-nums">{yen(order.tax)}</b></span>
                <span className="text-zinc-700 font-semibold">{t('shizai.order.total')} <b className="text-zinc-900 tabular-nums">{yen(order.total)}</b></span>
              </div>
            </div>

            {order.sent_at && (
              <p className="text-[11px] text-zinc-400">送信記録: {t(`shizai.send.${order.sent_via === 'mail' ? 'mail' : order.sent_via === 'post' ? 'post' : 'fax'}`)} · {order.sent_to || ''} · {String(order.sent_at).slice(0, 10)}</p>
            )}
            {order.approval_status === 'approved' && order.approved_by && (
              <p className="text-[11px] text-emerald-600" data-testid="order-approved-note">{t('shizai.approval.approved')}: {order.approved_by} · {String(order.approved_at || '').slice(0, 10)}</p>
            )}
            {order.approval_status === 'rejected' && (
              <p className="text-[11px] text-rose-500" data-testid="order-rejected-note">{t('shizai.approval.rejected')}: {order.rejected_by || ''}{order.reject_reason ? ` · ${order.reject_reason}` : ''}</p>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-1">
              {canApproveOrder && order.approval_status === 'pending' && (<>
                <Button size="sm" onClick={doApprove} data-testid="order-approve-btn" className="h-9 bg-teal-700 hover:bg-teal-800"><CheckCircle size={15} className="mr-1.5" weight="fill" /> {t('shizai.approval.approve')}</Button>
                <Button variant="outline" size="sm" onClick={doReject} data-testid="order-reject-btn" className="h-9 border-rose-200 text-rose-600 hover:bg-rose-50"><XCircle size={15} className="mr-1.5" /> {t('shizai.approval.reject')}</Button>
              </>)}
              <Button variant="outline" size="sm" onClick={printDocs} data-testid="order-print-btn" className="h-9"><Printer size={15} className="mr-1.5" /> {t('shizai.order.print')}</Button>
              <Button variant="outline" size="sm" onClick={() => (order.lines || []).length && setLabelData((order.lines || []).map((l) => buildProductTagLabel(order, l)))} data-testid="order-label-btn" className="h-9"><Tag size={15} className="mr-1.5" /> {t('mfg.label.print')}</Button>
              {canWrite && canEditOrder && <Button variant="outline" size="sm" onClick={() => onEdit?.(order)} data-testid="order-edit-btn" className="h-9"><PencilSimple size={15} className="mr-1.5" /> {t('shizai.edit')}</Button>}
              {canWrite && order.status !== 'cancelled' && order.status !== 'received' && (
                <Button variant="outline" size="sm" disabled={!order.can_send} title={!order.can_send ? t('shizai.approval.needApproval') : undefined} onClick={() => setPanel(panel === 'send' ? null : 'send')} data-testid="order-send-toggle" className="h-9 disabled:opacity-50"><PaperPlaneTilt size={15} className="mr-1.5" /> {t('shizai.order.send')}</Button>
              )}
              {canWrite && canReceive && (
                <Button variant="outline" size="sm" onClick={() => setPanel(panel === 'receive' ? null : 'receive')} data-testid="order-receive-toggle" className="h-9 border-emerald-200 text-emerald-600 hover:bg-emerald-50"><Package size={15} className="mr-1.5" /> {t('shizai.order.receive')}</Button>
              )}
              {canBridgeToAsset && (
                <Button variant="outline" size="sm" onClick={() => setRegisterOpen(true)}
                  data-testid="order-register-asset-btn"
                  className="h-9 border-indigo-300 text-indigo-700 hover:bg-indigo-50 font-semibold">
                  <Desktop size={15} className="mr-1.5" weight="duotone" /> IT資産として登録
                </Button>
              )}
              {(order.it_asset_ids || []).length > 0 && (
                <span className="inline-flex items-center h-9 px-2.5 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-semibold" data-testid="order-it-asset-count">
                  <Desktop size={13} className="mr-1" weight="duotone" /> IT資産 登録済み {(order.it_asset_ids || []).length}件
                </span>
              )}
              {canWrite && (
                <Button variant="outline" size="sm" onClick={togglePayment} data-testid="order-payment-btn" className="h-9"><CurrencyJpy size={15} className="mr-1.5" /> {order.payment_status === 'paid' ? t('shizai.order.markUnpaid') : t('shizai.order.markPaid')}</Button>
              )}
              {canWrite && canCancel && (
                <Button variant="ghost" size="sm" onClick={doCancel} data-testid="order-cancel-btn" className="h-9 text-rose-600 hover:bg-rose-50"><XCircle size={15} className="mr-1.5" /> {t('shizai.order.cancel')}</Button>
              )}
              {canDeleteOrder && !(order.lines || []).some((l) => (l.received_qty || 0) > 0) && (
                <Button variant="ghost" size="sm" onClick={doDelete} data-testid="order-delete-btn" className="h-9 text-rose-700 hover:bg-rose-50" title="発注を完全に削除します（依頼明細は未発注に戻ります）"><Trash size={15} className="mr-1.5" /> 削除</Button>
              )}
            </div>

            {/* Send panel */}
            {panel === 'send' && (
              <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3 space-y-3" data-testid="order-send-panel">
                <p className="text-xs text-zinc-500">{sendForm.via === 'mail' ? t('shizai.send.hintMail') : t('shizai.send.hint')}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs font-medium text-zinc-600">{t('shizai.send.via')}</Label>
                    <Select value={sendForm.via} onValueChange={(v) => setSendForm((p) => ({
                      ...p, via: v,
                      to: v === 'mail' ? (order.supplier?.contact_email || '') : (order.supplier?.fax || ''),
                    }))}>
                      <SelectTrigger data-testid="send-via-select" className="h-9 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="fax">{t('shizai.send.fax')}</SelectItem><SelectItem value="mail">{t('shizai.send.mail')}</SelectItem><SelectItem value="post">{t('shizai.send.post')}</SelectItem></SelectContent>
                    </Select></div>
                  <div><Label className="text-xs font-medium text-zinc-600">{sendForm.via === 'mail' ? t('shizai.send.toMail') : t('shizai.send.to')}</Label><Input data-testid="send-to-input" value={sendForm.to} onChange={(e) => setSendForm((p) => ({ ...p, to: e.target.value }))} className="h-9 mt-1" /></div>
                </div>
                <div><Label className="text-xs font-medium text-zinc-600">{t('shizai.send.note')}</Label><Input value={sendForm.note} onChange={(e) => setSendForm((p) => ({ ...p, note: e.target.value }))} className="h-9 mt-1" /></div>

                {/* 発注メール文面 — composed here, sent by the buyer from their own mail client */}
                {sendForm.via === 'mail' && (
                  <div className="rounded-md border border-zinc-200 bg-white p-3 space-y-2" data-testid="order-mail-draft">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-xs font-medium text-zinc-700">発注メール文面</p>
                      <span className="text-[11px] text-zinc-400">仕入先にそのまま送れる文面です（自動送信はしません）</span>
                      <div className="ml-auto flex items-center gap-1.5">
                        <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="order-mail-copy-btn"
                          onClick={() => {
                            const text = `件名: ${buildOrderMailSubject(order)}\n\n${mailBody}`;
                            navigator.clipboard.writeText(text)
                              .then(() => toast.success('文面をコピーしました'))
                              .catch(() => toast.error('コピーできませんでした'));
                          }}>
                          <Copy size={13} className="mr-1" /> コピー
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="order-mail-mailto-btn"
                          onClick={() => { window.location.href = buildOrderMailto(order, { to: sendForm.to, replyBy: sendForm.replyBy, senderName: user?.name, senderEmail: user?.email }); }}>
                          <EnvelopeSimple size={13} className="mr-1" /> メールソフトで開く
                        </Button>
                      </div>
                    </div>
                    <p className="text-[11px] text-zinc-500">件名: <span className="text-zinc-800">{buildOrderMailSubject(order)}</span></p>
                    <div className="flex items-center gap-2">
                      <Label className="text-[11px] text-zinc-500 shrink-0">回答期限</Label>
                      <DateField value={sendForm.replyBy || ''} testId="order-mail-replyby-input"
                        onChange={(v) => setSendForm((p) => ({ ...p, replyBy: v }))}
                        className="w-40" inputClassName="h-8 text-xs" />
                      <span className="hidden sm:inline text-[11px] text-zinc-400">未指定の場合は「3営業日以内」と記載されます</span>
                    </div>
                    <textarea readOnly value={mailBody} data-testid="order-mail-body"
                      className="w-full h-56 rounded-md border border-zinc-200 bg-zinc-50/60 p-2.5 text-[12px] leading-relaxed text-zinc-800 font-mono" />
                  </div>
                )}

                <div className="flex justify-end"><Button size="sm" onClick={doSend} disabled={busy} data-testid="send-confirm-btn" className="h-9">{t('shizai.send.confirm')}</Button></div>
              </div>
            )}

            {/* Receive panel */}
            {panel === 'receive' && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-3" data-testid="order-receive-panel">
                <p className="text-xs text-zinc-500">{t('shizai.receive.toShelf')}</p>
                <div className="max-w-xs"><Label className="text-xs font-semibold text-zinc-600">{t('shizai.receive.location')}</Label>
                  <Select value={recvForm.location_id} onValueChange={(v) => setRecvForm((p) => ({ ...p, location_id: v }))}>
                    <SelectTrigger data-testid="receive-location-select" className="h-9 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.code}{l.name ? ` — ${l.name}` : ''}</SelectItem>)}</SelectContent>
                  </Select></div>
                <table className="w-full text-xs">
                  <thead><tr className="text-zinc-500 border-b border-emerald-200">
                    <th className="text-left font-semibold py-1.5">{t('shizai.req.name')}</th>
                    <th className="text-right font-semibold py-1.5">{t('shizai.receive.remaining')}</th>
                    <th className="text-right font-semibold py-1.5 w-32">{t('shizai.receive.qtyThisTime')}</th>
                  </tr></thead>
                  <tbody>
                    {(order.lines || []).map((l, i) => {
                      const remaining = (l.qty || 0) - (l.received_qty || 0);
                      const pkRem = packOf({ ...l, qty: remaining });
                      return (
                        <tr key={`${l.part_no || l.name || ''}-${i}`} className="border-t border-emerald-100">
                          <td className="py-1.5 text-zinc-700">{l.name || l.part_no || '—'}</td>
                          <td className="py-1.5 text-right tabular-nums">{remaining} {l.uom}{pkRem && <span className="text-[10px] text-zinc-400"> ({pkRem.packs}{pkRem.packUom})</span>}</td>
                          <td className="py-1.5 text-right">
                            <Input type="number" data-testid={`receive-qty-${i}`} disabled={remaining <= 0}
                              value={recvForm.lines[i] ?? ''} onChange={(e) => setRecvForm((p) => ({ ...p, lines: { ...p.lines, [i]: e.target.value } }))}
                              className="h-8 w-28 ml-auto text-right text-xs" placeholder="0" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="flex justify-end"><Button size="sm" onClick={doReceive} disabled={busy} data-testid="receive-confirm-btn" className="h-9 bg-teal-700 hover:bg-teal-800">{t('shizai.receive.confirm')}</Button></div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
    <LabelPrintDialog open={!!labelData} onClose={() => setLabelData(null)} labels={labelData || []} title="現品票ラベル" />
    {order && (
      <RegisterAsAssetDialog
        open={registerOpen}
        order={order}
        onClose={() => setRegisterOpen(false)}
        onRegistered={() => { setRegisterOpen(false); toast.success('IT資産として登録しました'); load(); onChanged?.(); }}
      />
    )}
    </>
  );
}
